import {
  betStore,
  popupStore,
  userInfoStore,
  roundInfoStore,
  gameResultStore,
  gameConfigStore,
  profileAvatarStore,
  freeBetStore,
  loadingStore,
} from '@pascal/pascal-core/shared/core/store';

import {
  RoundStatus,
  iframeService,
  notificationStore,
  parseNumberByPrecision,
  userSettingsStore,
} from '@pascal/pascal-core';

import { worker } from '../index';
import { gameName } from '../enums';
import { liveConnection } from '../core';
import { getStorageItem } from '../helpers';
import { gameLiveConnection } from './GameConnection';
import { soundService, soundTypes } from './soundService';
import { doBetStore, gameStore, roundTimeStore } from '../store';

let timeOutId;

class GameService {
  onGetInitialStateResult({
    rate,
    limits,
    results,
    balance,
    avatarId,
    precision,
    roundInfo,
    currentBets,
    currencyCode,
    gameConfiguration,
  }) {
    const { Started, Finished, OnFinish } = RoundStatus;
    const { status, second } = roundInfo;

    roundTimeStore.resetRoundTimeData();
    gameStore.setIsBetsClosed(false);
    betStore.setLiveBets([]);
    if (second < 0) {
      roundTimeStore.setPassedSeconds(second);
    }

    gameStore.setGameState({
      status,
      isInitialLoaded: true,
    });

    if (currentBets && currentBets?.length) {
      betStore.setLiveBets(currentBets);
    }

    if (status === Started) {
      const isBetClosed = second < 0 || second === 0;
      if (isBetClosed) {
        doBetStore.updateIsDisabledBetSection(true);
        gameStore.setIsBetsClosed(true);
      }
      doBetStore.setWinAmount('');
      gameStore.setFinishedResult(null);
    } else if (status === Finished || status === OnFinish) {
      let currentTotalWinAmount;
      if (currentBets && currentBets.length) {
        currentTotalWinAmount = currentBets
          .filter(
            (item) => item?.playerExternalId === userInfoStore.playerExternalId && !item.winAmount
          )
          .reduce((acc, curr) => acc + parseNumberByPrecision(Number(curr?.winAmount), 1, 2), 0);
      }
      doBetStore.setWinAmount(currentTotalWinAmount);
    }

    userSettingsStore.setIsAnimationOn(
      getStorageItem(`${gameName}_userSettings`) || userSettingsStore.isAnimationOn
    );

    gameStore.setMargin(+gameConfiguration.margin);
    gameResultStore.setGameResults(results);
    profileAvatarStore.updateInitialAvatarId(avatarId);

    roundInfoStore.setRoundInfo(roundInfo);
    gameConfigStore.setLimits(limits);

    const { defaultBetAmount, minBet } = limits;
    iframeService.sendStakeUpdate(defaultBetAmount || minBet);

    const data = gameResultStore.gameResults.filter((result) => result.roundData).slice(0, 7);
    gameStore.setPastRoundsData(data);

    doBetStore.setBetAmount(
      freeBetStore?.activeFreeBet.id
        ? freeBetStore?.activeFreeBet?.amount
        : defaultBetAmount || minBet
    );

    if (RoundStatus.Started === roundInfo.status && roundInfo.second > 0) {
      gameStore.setTimerDuration(roundInfo.second);
      const time = Math.floor(Math.abs(roundInfo.second));
      roundTimeStore.setTime(time);
      this.sendWorker(time);
    } else {
      this.sendWorker();
      gameStore.setTimerDuration(roundInfo.duration);
      roundTimeStore.setTime(null);
    }

    loadingStore.updateLoading({ isGameInitialLoaded: true });
    popupStore.close();
    popupStore.toggleReconnect(false);

    userInfoStore.setUserInfo({
      rate,
      balance,
      precision,
      currencyCode,
    });
  }

  handleDoBetResult(data) {
    const { userId } = userInfoStore;
    const { isSoundOn } = userSettingsStore;
    if (Number(userId) === Number(+data.userId)) {
      // if (data.freeBetId) {
      //   freeBetStore.handleFreeBetDoBetResult(data.freeBetId);
      // }
      if (isSoundOn) {
        soundService.playBetAccepted();
      }
      doBetStore.handleDoBetResult(data);
      doBetStore.updateIsDisabledBetSection(false);
    }

    betStore.updateLiveBets(data);
  }

  handleDoBetResultValidation(data) {
    const { responseCode } = data;
    if (doBetStore.betData.isDisabled) {
      doBetStore.updateIsDisabledBetSection(false);
    }
    doBetStore.setIsLoadingBetButton(false);
    notificationStore.setNotificationType({ responseCode });
  }

  handleRoundStart({ data: { startedRound, results } }) {
    const { roundId } = startedRound;

    gameStore.setIsBetsClosed(false);
    gameStore.setStopLight(false);
    roundTimeStore.setPassedSeconds(null);

    gameStore.setGameState({
      status: RoundStatus.Started,
      isInitialLoaded: false,
    });

    this.sendWorker(roundInfoStore.duration);
    doBetStore.setWinAmount('');
    gameStore.setFinishedResult(null);
    gameResultStore.setGameResults(results);
    betStore.setLiveBets([]);

    roundInfoStore.setRoundId(roundId);
    this.betData = {
      ...this.betData,
      isBetAccepted: false,
      isDisabled: false,
    };
    // freeBetStore.handleFreeBetEndedData();
  }

  handleRoundFin({ data }) {
    const { bets, finishedRound } = data;
    const { userId } = userInfoStore;
    let updatedRealLiveBets = [];

    soundService.play(soundTypes.switchNumber);

    // if (isDemo) {
    //   demoBets = betStore?.liveBets
    //     ?.filter((item) => !item?.playerExternalId)
    //     .map((item) => {
    //       const { marketType, number } = item.betData;
    //       let isWon;
    //       if (marketType === 0) {
    //         isWon = finishedRound.roundData <= +number;
    //       } else {
    //         isWon = finishedRound.roundData >= +number;
    //       }
    //       const demoWinAmount = item.odd * item.amount;
    //       const winAmount = isWon ? Number(demoWinAmount) : 0;
    //       return {
    //         ...item,
    //         status: isWon ? Win : Lose,
    //         winAmount,
    //         roundData: finishedRound.roundData,
    //       };
    //     });
    //   const currentTotalWinAmount = demoBets
    //     ?.filter((item) => item?.status === Win)
    //     .reduce((acc, curr) => acc + curr.winAmount, 0);
    //
    //   if (currentTotalWinAmount) {
    //     let validDemoWinAmount;
    //     if (Number(currentTotalWinAmount) > maxWin) {
    //       validDemoWinAmount = maxWin;
    //       notificationStore.setNotificationType({ responseCode: BetResponseCode.MaxWinPayout });
    //     } else {
    //       validDemoWinAmount = Number(currentTotalWinAmount);
    //     }
    //     setTimeout(() => {
    //       soundService.play(soundTypes.winSound);
    //       doBetStore.setWinAmount(parseNumberByPrecision(1, validDemoWinAmount));
    //       doBetStore.increaseDemoBalance(validDemoWinAmount, 1);
    //     }, 2050);
    //   }
    // }
    gameStore.setFinishedResult(+finishedRound.roundData);
    setTimeout(() => {
      gameStore.setStopLight(true);
    }, 1887);

    if (bets.length) {
      const myBets = bets.filter((bet) => bet.userId === +userId);
      if (myBets.length) {
        const myWinAmount = myBets.reduce(
          (acc, curr) => acc + parseNumberByPrecision(Number(curr?.winAmount), 1, 2),
          0
        );

        setTimeout(() => {
          soundService.play(soundTypes.winSound);
          soundService.playWin();
          doBetStore.setWinAmount(myWinAmount);
        }, 2050);
      }
      updatedRealLiveBets = bets.map((item) => {
        return {
          ...item,
          betData: { ...item?.additionalParams },
        };
      });
      // bets.forEach((item) => {
      //   if (
      //     item?.playerExternalId === userInfoStore.playerExternalId &&
      //     item.status === BetStatus.Win
      //   ) {
      //     freeBetStore.freeBetTotalWinCalculator(item);
      //   }
      // });
    }

    setTimeout(() => {
      betStore.setLiveBets([...updatedRealLiveBets]);
      gameStore.updatedPastRoundsData({ roundId: finishedRound.id, ...finishedRound });
    }, 2050);

    // if (isDemo) {
    //   setTimeout(() => betStore.updateDemoBetHistory(demoBets), 2050);
    //   doBetStore.updateTotalBetsAmount(0, true);
    // }
    // freeBetStore.handleFreeBetEndedData();
  }

  handleBetsClosed() {
    gameStore.setIsBetsClosed(true);

    soundService.play(soundTypes.lightOn);

    gameStore.setGameState({
      status: '',
      isInitialLoaded: false,
    });
    doBetStore.setIsLoadingBetButton(false);
    doBetStore.updateIsDisabledBetSection(true);

    // if (isDemo) {
    //   doBetStore.updateIsDisabledBetSection(true);
    // }
    freeBetStore.handleFreeBetEndedData();
  }

  handleCashOutResult() {}

  countDown({ data: time }) {
    roundTimeStore.setTime(time);
  }

  handleReconnecting(error) {
    if (error && error.message) {
      popupStore.toggleReconnect();
    } else {
      GameService.ReconnectCount += 1;
      popupStore.toggleReconnect();
    }
  }

  handleReconnected() {
    GameService.ReconnectCount = 0;
    gameLiveConnection.sendGameGetInitialState(false);
  }

  handleConnectionError() {
    if (GameService.ReconnectCount) {
      popupStore.onConnectionError(true);
    }
  }

  socketRecursiveReconnecting() {
    try {
      liveConnection
        .restartGameConnection()
        .then((connectionData) => {
          liveConnection.connectionInitCallback(connectionData);
          clearTimeout(timeOutId);
        })
        .catch(() => {
          timeOutId = setTimeout(() => {
            this.socketRecursiveReconnecting();
          }, 4000);
        });
    } catch (err) {
      popupStore.onConnectionError(true);
    }
  }

  sendWorker(timerValue) {
    if (timerValue) {
      worker.addEventListener('message', this.countDown);
      worker.postMessage(timerValue);
    } else {
      worker.removeEventListener('message', this.countDown);
    }
  }
}

export const gameService = new GameService();
